/**
 * useWorkspaceTweaks — V2 tweaks-panel state with localStorage round-trip.
 *
 * Cycle-109 sprint-204 S2.7 (2026-05-21). Implements AC-2.7.c.
 *
 * Contract: SDD §4.1 (`WorkspaceTweaks` shape) + §4.2
 * (storage key `echelon.workspace.tweaks`). Defaults are the canonical
 * `Workspace.html:241-253` snapshot — terminal modules, notched panels,
 * stipple globe, compact density, forge lane on, etc.
 *
 * Returns `[tweaks, setTweak, resetTweaks]`:
 *   - `tweaks: WorkspaceTweaks` — current snapshot (always defined).
 *   - `setTweak<K extends keyof WorkspaceTweaks>(key, value)` — patch one
 *     field and persist. Type-safe per key.
 *   - `resetTweaks()` — restore canonical defaults and persist.
 *
 * Storage failures (Safari private mode, quota exceeded) silently degrade
 * to in-memory state.
 */

import { useCallback, useEffect, useState } from 'react';

export const WORKSPACE_TWEAKS_STORAGE_KEY = 'echelon.workspace.tweaks';

export interface WorkspaceTweaks {
  theme: 'dark' | 'light';
  globeStyle: 'stipple' | 'wireframe';
  density: 'compact' | 'comfortable';
  signalRail: boolean;
  showArcs: boolean;
  moduleStyle: 'terminal' | 'palantir';
  panelStyle: 'notched' | 'flat';
  autoRotate: boolean;
  forgeLane: boolean;
  forgeDerivedTags: boolean;
}

export const DEFAULT_WORKSPACE_TWEAKS: WorkspaceTweaks = {
  theme: 'dark',
  globeStyle: 'stipple',
  density: 'compact',
  signalRail: true,
  showArcs: true,
  moduleStyle: 'terminal',
  panelStyle: 'notched',
  autoRotate: false,
  forgeLane: true,
  forgeDerivedTags: true,
};

const ENUM_VALUES = {
  theme: new Set<WorkspaceTweaks['theme']>(['dark', 'light']),
  globeStyle: new Set<WorkspaceTweaks['globeStyle']>(['stipple', 'wireframe']),
  density: new Set<WorkspaceTweaks['density']>(['compact', 'comfortable']),
  moduleStyle: new Set<WorkspaceTweaks['moduleStyle']>(['terminal', 'palantir']),
  panelStyle: new Set<WorkspaceTweaks['panelStyle']>(['notched', 'flat']),
} as const;

function validateTweaks(input: unknown): WorkspaceTweaks | null {
  if (!input || typeof input !== 'object') return null;
  const candidate = input as Partial<Record<keyof WorkspaceTweaks, unknown>>;
  // Coerce field-by-field. Any invalid field falls back to the canonical
  // default for that field — partial corruption doesn't nuke the whole map.
  const out: WorkspaceTweaks = { ...DEFAULT_WORKSPACE_TWEAKS };
  for (const [key, enumSet] of Object.entries(ENUM_VALUES) as [
    keyof typeof ENUM_VALUES,
    Set<string>,
  ][]) {
    const v = candidate[key];
    if (typeof v === 'string' && enumSet.has(v)) {
      (out as unknown as Record<string, unknown>)[key] = v;
    }
  }
  for (const key of [
    'signalRail',
    'showArcs',
    'autoRotate',
    'forgeLane',
    'forgeDerivedTags',
  ] as const) {
    const v = candidate[key];
    if (typeof v === 'boolean') out[key] = v;
  }
  return out;
}

function readStoredTweaks(): WorkspaceTweaks {
  if (typeof window === 'undefined') return DEFAULT_WORKSPACE_TWEAKS;
  try {
    const raw = window.localStorage.getItem(WORKSPACE_TWEAKS_STORAGE_KEY);
    if (!raw) return DEFAULT_WORKSPACE_TWEAKS;
    const parsed = JSON.parse(raw) as unknown;
    const validated = validateTweaks(parsed);
    return validated ?? DEFAULT_WORKSPACE_TWEAKS;
  } catch {
    return DEFAULT_WORKSPACE_TWEAKS;
  }
}

function persist(tweaks: WorkspaceTweaks): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(WORKSPACE_TWEAKS_STORAGE_KEY, JSON.stringify(tweaks));
  } catch {
    /* Quota exceeded etc. — in-memory state continues. */
  }
}

export interface UseWorkspaceTweaksResult {
  readonly tweaks: WorkspaceTweaks;
  readonly setTweak: <K extends keyof WorkspaceTweaks>(
    key: K,
    value: WorkspaceTweaks[K],
  ) => void;
  readonly resetTweaks: () => void;
}

export function useWorkspaceTweaks(): UseWorkspaceTweaksResult {
  const [tweaks, setTweaksState] = useState<WorkspaceTweaks>(() => readStoredTweaks());

  // Cross-tab sync — pick up updates from other tabs / windows.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent) {
      if (e.key !== WORKSPACE_TWEAKS_STORAGE_KEY) return;
      setTweaksState(readStoredTweaks());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTweak = useCallback(
    <K extends keyof WorkspaceTweaks>(key: K, value: WorkspaceTweaks[K]) => {
      setTweaksState((prev) => {
        if (prev[key] === value) return prev;
        const next = { ...prev, [key]: value };
        persist(next);
        return next;
      });
    },
    [],
  );

  const resetTweaks = useCallback(() => {
    setTweaksState(DEFAULT_WORKSPACE_TWEAKS);
    persist(DEFAULT_WORKSPACE_TWEAKS);
  }, []);

  return { tweaks, setTweak, resetTweaks };
}
